/**
 * Every finding on a definition, as one list for the 规则缺陷 tab.
 *
 * Two sources plus three checks that only make sense with a parse tree:
 *   - `projection.issues` — the structural and rule checks the canvas already runs
 *   - `auditDefinition` (ast.ts) — expressions the grammar cannot parse, and fallback
 *     chains with arithmetic inside (`suspiciousCoalesce`, the live Q1.1.4.1 precedence bug)
 *   - `anymatch-always-true` — `hasAnyMatch` over a set that contains every option of the
 *     question, which is true for ANY answer (VIP / Compete Funding on q_1_1 and q_1_2)
 *   - `ref-after-owner` — an eligibility rule that reads a question asked AFTER its owner;
 *     sound only because the backend evaluates at submit with the full answer set, and the
 *     first thing to break if anything ever evaluates "standing on the question" (info)
 *   - `option-label-value-mismatch` — an option labelled `e)` whose value is `f`; rules use
 *     the value, the live frontend's combination check used the label (Q1.1.3.1 / Q1.2.3.1)
 */
import type { QuestionnaireDefinition } from '../types';
import { auditDefinition, parse, walk } from './ast';
import type { Issue, IssueSeverity, Projection } from './projection';

/**
 * Severity tiers, as the 规则缺陷 tab shows them:
 *   缺陷 (error)        definitely wrong — the rule cannot do what it says
 *   可疑 (warning)      very likely wrong, e.g. a fallback chain missing its parentheses
 *   需确认 (info + needsConfirm)  real behaviour that only the business can call a bug
 *   提示 (info)         sound today, fragile under a plausible change
 * Only the first two are counted in the badge or marked on the canvas.
 */

export interface IssueRow {
  id: string;
  severity: IssueSeverity;
  code: string;
  nodeId?: string;
  rule?: Issue['rule'];
  message: string;
  /** The expression the finding is about, when there is one. */
  expression?: string;
  /**
   * The behaviour is real but whether it is WRONG is a business call — a rule that grants
   * VIP Funding to everyone may be intended. Listed as 「需确认」, never counted or marked as
   * a defect. Always paired with `severity: 'info'`.
   */
  needsConfirm?: boolean;
}

/** Display order: 缺陷, 可疑, 需确认, 提示. */
const rank = (r: IssueRow) => (r.severity === 'error' ? 0 : r.severity === 'warning' ? 1 : r.needsConfirm ? 2 : 3);

/** What counts towards the tab badge and the canvas marks: definite or likely defects only. */
export const isCounted = (r: IssueRow) => r.severity === 'error' || r.severity === 'warning';

function ruleExpression(def: QuestionnaireDefinition, nodeId?: string, rule?: Issue['rule']): string | undefined {
  if (!nodeId || !rule) return undefined;
  const n = def.nodes.find((x) => x.nodeId === nodeId);
  const list =
    rule.kind === 'program' ? n?.programRules : rule.kind === 'nav' ? n?.nextQuestionRules : n?.autoAnswerRules;
  return list?.[rule.index]?.expression.trim();
}

export function issueListOf(def: QuestionnaireDefinition, projection: Projection): IssueRow[] {
  const rows: IssueRow[] = projection.issues.map((i) => ({
    ...i,
    expression: ruleExpression(def, i.nodeId, i.rule),
  }));
  const add = (r: Omit<IssueRow, 'id'>) => rows.push({ ...r, id: `${r.code}:${r.nodeId ?? '-'}:${rows.length}` });
  const kindOf = { next: 'nav', program: 'program', auto: 'auto' } as const;
  const byQ = new Map(def.nodes.filter((n) => n.qualifiedNodeId).map((n) => [n.qualifiedNodeId!, n]));
  const layerOf = new Map(
    projection.nodes.filter((n) => n.kind === 'question').map((n) => [n.id, n.layer] as const),
  );

  for (const a of auditDefinition(def)) {
    const rule = { kind: kindOf[a.kind], index: a.index };
    if (a.error) {
      add({
        severity: 'error',
        code: 'parse-error',
        nodeId: a.nodeId,
        rule,
        message: `「${a.nodeId}」的第 ${a.index + 1} 条${a.kind === 'program' ? '资格规则' : a.kind === 'next' ? '条件边' : '自动作答规则'}超出了表达式语法：${a.error}`,
        expression: a.source,
      });
      continue;
    }
    if (a.suspicious) {
      add({
        severity: 'warning',
        code: 'suspicious-coalesce',
        nodeId: a.nodeId,
        rule,
        message: `「${a.nodeId}」的第 ${a.index + 1} 条规则里，兜底链 \`||\` 中夹着算术：\`+\` 比 \`||\` 结合更紧，\`a || b + c || d\` 是真值链而不是「(a||b) + (c||d)」求和 —— 疑似漏了括号。`,
        expression: a.source,
      });
    }
    const tree = parse(a.source);
    for (const x of walk(tree)) {
      if (x.k !== 'anyMatch') continue;
      const target = byQ.get(x.ref);
      const values = (target?.answerOptions ?? []).map((o) => o.value);
      if (values.length > 0 && values.every((v) => x.vs.includes(v))) {
        add({
          severity: 'info',
          needsConfirm: true,
          code: 'anymatch-always-true',
          nodeId: a.nodeId,
          rule,
          message: `「${a.nodeId}」的第 ${a.index + 1} 条规则用 hasAnyMatch 匹配「${target!.nodeId}」的全部 ${values.length} 个选项 —— 只要答了这题就为真（包括「没有」类选项），等于没有条件。确认是否本意。`,
          expression: a.source,
        });
      }
    }
    if (a.kind === 'program') {
      const own = layerOf.get(a.nodeId);
      const later = [...new Set(walk(tree).flatMap((x) => (x.k === 'var' || x.k === 'anyMatch' ? [x.ref] : [])))]
        .map((q) => byQ.get(q))
        .filter((n) => n && own !== undefined && (layerOf.get(n.nodeId) ?? -1) > own);
      if (later.length) {
        add({
          severity: 'info',
          code: 'ref-after-owner',
          nodeId: a.nodeId,
          rule,
          message: `「${a.nodeId}」的第 ${a.index + 1} 条资格规则读了之后才问的 ${later.map((n) => n!.nodeId).join('、')}。只因后端在 Submit 时拿完整答案求值才成立；任何「站在当前题上」求值的实现都会读到 undefined。`,
          expression: a.source,
        });
      }
    }
  }

  for (const n of def.nodes) {
    const bad = (n.answerOptions ?? []).filter((o) => {
      const lead = /^\s*([a-z])\)/.exec(o.optionText.en || o.optionText.cn || '')?.[1];
      return !!lead && lead !== o.value;
    });
    if (bad.length) {
      add({
        severity: 'info',
        needsConfirm: true,
        code: 'option-label-value-mismatch',
        nodeId: n.nodeId,
        message: `「${n.nodeId}」的选项标号与 value 不一致：${bad.map((o) => `「${(o.optionText.cn || o.optionText.en).trim()}」的 value 是 ${o.value}`).join('；')}。规则按 value 写，读题的人按标号读。`,
      });
    }
  }

  return rows.sort((a, b) => rank(a) - rank(b));
}
